import React, { FC, useCallback, useEffect } from 'react'
import { FormControl, Grid, InputLabel, TextField, Typography } from '@mui/material'
import { CrmUserNewPasswordRequestDto } from '@sparling/financial-common'
import { FormProvider, useForm } from 'react-hook-form'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { withStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { GreenButton } from '../../components/Buttons/GreenButton'
import useStyles from './useStyle'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'

const resolver = classValidatorResolver(CrmUserNewPasswordRequestDto)

interface IProps {
  model: CrmUserNewPasswordRequestDto
  onSubmit: (newValue: unknown) => void
}

const CustomFormControl = withStyles((theme: Theme) => ({
  root: {
    '& label': {
      color: theme.palette.primary.light,
      opacity: '60%',
    },
    '& input:valid': {
      fontSize: 16,
      border: '1px solid rgba(255,255,255,0.6)',
      borderRadius: 4,
      padding: 10,
    },
    '& input:valid:hover + fieldset': {
      fontSize: 16,
      border: `1px solid ${theme.palette.primary.light}`,
    },
    '& input:valid:active + fieldset': {
      fontSize: 16,
      border: `1px solid ${theme.palette.secondary.main}`,
    },
    '& input:valid + fieldset': {
      fontSize: 16,
      opacity: 0.6,
      color: theme.palette.primary.light,
    },
    '& fieldset': {
      color: 'red',
    },
  },
}))(FormControl)

export const ResetPasswordForm: FC<IProps> = (props): JSX.Element => {
  const classes = useStyles()
  const { onSubmit } = props
  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })
  const {
    handleSubmit,
    register,
    formState: { isValid, errors },
    setValue,
  } = formMethods

  useEffect(() => {
    setValue('password', '')
    setValue('confirmPassword', '')
  }, [setValue])

  const errorMessage = useCallback(
    (inputName: string) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const error = errors[inputName]
      const message = error?.message.replace(inputName, '')
      return capitalizeFirstLetter(message)
    },
    [errors],
  )

  return (
    <FormProvider {...formMethods}>
      <Grid container className={classes.containerForm}>
        <Typography variant={'h2'}>Reset Password</Typography>

        <form className={classes.form} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Grid container className={classes.formWrap}>
            <Grid item xs={12} className={classes.inputWrap}>
              <CustomFormControl className={classes.formControl}>
                <InputLabel shrink htmlFor="password" className={classes.label}>
                  Password
                </InputLabel>
                <TextField
                  className={classes.input}
                  id="password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  {...register('password')}
                />
              </CustomFormControl>
              {errors.username && (
                <Typography variant="body1" gutterBottom className={classes.errorField}>
                  {errorMessage('password')}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} className={classes.inputWrap}>
              <CustomFormControl className={classes.formControl}>
                <InputLabel shrink htmlFor="confirmPassword" className={classes.label}>
                  Confirm Password
                </InputLabel>
                <TextField
                  className={classes.input}
                  id="confirmPassword"
                  type="password"
                  variant="outlined"
                  fullWidth
                  {...register('confirmPassword')}
                />
              </CustomFormControl>
              {errors.username && (
                <Typography variant="body1" gutterBottom className={classes.errorField}>
                  {errorMessage('confirmPassword')}
                </Typography>
              )}
            </Grid>

            <GreenButton disabled={!isValid} type="submit" width={320} mt={32} text="Save Password" />
          </Grid>
        </form>
      </Grid>
    </FormProvider>
  )
}
